/**
 * Change Log Drawer Component
 * 
 * Displays versioning/change history for exhibits (EX2-EX9)
 * Uses RightContentDrawer as base component
 * Shows timeline-style list grouped by date
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { htmlToPlainText } from '../../../utils/exportUtils';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import { RootState, AppDispatch } from '../../../store';
import {
  ActionType,
  ParsedChangeLogItem,
  getChangeLog,
  groupChangeLogByDate,
} from '../../../services/changeLogService';
import {
  setFinancialStatementChanges,
  setHierarchyChanges,
  setRiskAssessmentChanges,
  setControlChanges,
  setGapChanges,
  setCertificationChanges,
  setComplianceChanges,
  setAuditPlanChanges,
} from '../../../store/slices/changeLogSlice';

// ==================== STYLED COMPONENTS ====================

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DateGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateHeader = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 13px;
  color: #1F2937;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 16px;
`;

const TimelineItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 12px;
  position: relative;
`;

const TimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 60px;
  padding-top: 2px;
`;

const TimeText = styled.span`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  color: #6B7280;
`;

const TimelineTrack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const TimelineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #9CA3AF;
  flex-shrink: 0;
  margin-top: 6px;
  z-index: 1;
`;

const TimelineLine = styled.div`
  width: 2px;
  flex: 1;
  background: #E5E7EB;
  min-height: 20px;
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 20px;
  border-bottom: 1px solid #F3F4F6;
  margin-bottom: 16px;
`;

const ItemTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const ItemDescription = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  line-height: 1.5;
`;

const ValueChangeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: Inter;
  font-size: 12px;
  margin-top: 4px;
  padding: 8px 12px;
  background: #F9FAFB;
  border-radius: 6px;
  border: 1px solid #E5E7EB;
`;

const ValueContent = styled.div`
  flex: 1;
  min-width: 0;
  word-break: break-word;
  
  /* Reset HTML content styles */
  p, div, span {
    margin: 0;
    padding: 0;
  }
  
  p:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const OldValue = styled(ValueContent)`
  color: #EF4444;
  text-decoration: line-through;
`;

const NewValue = styled(ValueContent)`
  color: #10B981;
  font-weight: 500;
`;

const ArrowIcon = styled.span`
  color: #9CA3AF;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding-top: 2px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const UserAvatar = styled(Avatar)`
  background: #10B981;
`;

const UserName = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #1F2937;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 20px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top-color: #2563EB;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const ShowOlderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  border: none;
  background: transparent;
  color: #2563EB;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #1D4ED8;
  }

  svg {
    transition: transform 0.2s;
  }

  &:hover svg {
    transform: translateY(2px);
  }
`;

const EmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 100px 20px;
`;

const EmptyText = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 15px;
  color: #1F2937;
  text-align: center;
`;

// ==================== PROPS INTERFACE ====================

export interface ChangeLogDrawerProps {
  open: boolean;
  onClose: () => void;
  actionType: ActionType;
  annualReportId: string;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Get cached data from Redux based on action type
 */
const getCachedData = (
  state: RootState,
  actionType: ActionType
): ParsedChangeLogItem[] | null => {
  switch (actionType) {
    case 'financialStatement':
      return state.changeLog.financialStatement;
    case 'hierarchy':
      return state.changeLog.hierarchy;
    case 'riskAssessment':
      return state.changeLog.riskAssessment;
    case 'control':
      return state.changeLog.control;
    case 'gap':
      return state.changeLog.gap;
    case 'certification':
      return state.changeLog.certification;
    case 'compliance':
      return state.changeLog.compliance;
    case 'auditPlan':
      return state.changeLog.auditPlan;
    default:
      return null;
  }
};

/**
 * Get the action creator for setting cache based on action type
 */
const getSetCacheAction = (actionType: ActionType) => {
  switch (actionType) {
    case 'financialStatement':
      return setFinancialStatementChanges;
    case 'hierarchy':
      return setHierarchyChanges;
    case 'riskAssessment':
      return setRiskAssessmentChanges;
    case 'control':
      return setControlChanges;
    case 'gap':
      return setGapChanges;
    case 'certification':
      return setCertificationChanges;
    case 'compliance':
      return setComplianceChanges;
    case 'auditPlan':
      return setAuditPlanChanges;
    default:
      return setFinancialStatementChanges;
  }
};

// ==================== MAIN COMPONENT ====================

const ChangeLogDrawer: React.FC<ChangeLogDrawerProps> = ({
  open,
  onClose,
  actionType,
  annualReportId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Get cached data from Redux
  const cachedData = useSelector((state: RootState) => 
    getCachedData(state, actionType)
  );
  
  // Local loading state (not stored in Redux)
  const [loading, setLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(20);
  
  // Data to display - either from cache or fetched
  const [data, setData] = useState<ParsedChangeLogItem[]>([]);
  
  // Track if we've already fetched for current open session
  const hasFetchedRef = useRef(false);
  
  // Fetch change log data
  const fetchData = useCallback(async (showLoading: boolean = true) => {
    if (!annualReportId) return;
    
    if (showLoading) {
      setLoading(true);
    }
    
    try {
      const response = await getChangeLog(annualReportId, actionType);
      setData(response.items);
      
      // Update cache in Redux
      const setCacheAction = getSetCacheAction(actionType);
      dispatch(setCacheAction(response.items));
    } catch (error: any) {
      console.error('Failed to fetch change log:', error);
      message.error(error?.message || 'Failed to load change history');
    } finally {
      setLoading(false);
    }
  }, [annualReportId, actionType, dispatch]);
  
  // Handle drawer open
  useEffect(() => {
    if (open) {
      // Reset display count when opening
      setDisplayCount(20);
      
      // Check if we have cached data (not null)
      const hasCache = cachedData !== null;
      
      if (hasCache) {
        // Show cached data immediately
        setData(cachedData);
        
        // Only refetch in background once per open session
        if (!hasFetchedRef.current) {
          hasFetchedRef.current = true;
          fetchData(false);
        }
      } else {
        // No cache - show loading and fetch
        setData([]);
        hasFetchedRef.current = true;
        fetchData(true);
      }
    } else {
      // Reset fetch flag when drawer closes
      hasFetchedRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  
  // Group data by date
  const groupedData = groupChangeLogByDate(data.slice(0, displayCount));
  
  // Show more items
  const handleShowOlder = () => {
    setDisplayCount(prev => prev + 20);
  };
  
  const hasMoreItems = data.length > displayCount;
  
  return (
    <RightContentDrawer
      open={open}
      onClose={onClose}
      title="Versioning"
      width={520}
      minWidth={400}
      maxWidth={700}
    >
      {loading && data.length === 0 ? (
        <LoadingWrapper>
          <LoadingSpinner />
          <LoadingText>Loading change history...</LoadingText>
        </LoadingWrapper>
      ) : data.length === 0 ? (
        <EmptyWrapper>
          <EmptyText>There are no records to display yet.</EmptyText>
        </EmptyWrapper>
      ) : (
        <ContentWrapper>
          {Array.from(groupedData.entries()).map(([date, items]) => (
            <DateGroup key={date}>
              <DateHeader>{date}</DateHeader>
              <TimelineItemsContainer>
                {items.map((item, index) => (
                  <TimelineItem key={item.id}>
                    <TimeColumn>
                      <TimeText>{item.formattedTime}</TimeText>
                    </TimeColumn>
                    <TimelineTrack>
                      <TimelineDot />
                      {index < items.length - 1 && <TimelineLine />}
                    </TimelineTrack>
                    <ItemContent>
                      <ItemTitle>{item.title}</ItemTitle>
                      <ItemDescription>{item.description}</ItemDescription>
                      {(item.oldValue || item.newValue) && (
                        <ValueChangeRow>
                          <OldValue>
                            {htmlToPlainText(item.oldValue) || <em>(empty)</em>}
                          </OldValue>
                          <ArrowIcon>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <path 
                                d="M2 7H12M12 7L8 3M12 7L8 11" 
                                stroke="currentColor" 
                                strokeWidth="1.5" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              />
                            </svg>
                          </ArrowIcon>
                          <NewValue>
                            {htmlToPlainText(item.newValue) || <em>(empty)</em>}
                          </NewValue>
                        </ValueChangeRow>
                      )}
                      <UserInfo>
                        <UserAvatar size={24} icon={<UserOutlined />} />
                        <UserName>{item.actionedByName}</UserName>
                      </UserInfo>
                    </ItemContent>
                  </TimelineItem>
                ))}
              </TimelineItemsContainer>
            </DateGroup>
          ))}
          
          {hasMoreItems && (
            <ShowOlderButton onClick={handleShowOlder}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path 
                  d="M4 6L8 10L12 6" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
              Show older
            </ShowOlderButton>
          )}
          
          {loading && data.length > 0 && (
            <LoadingWrapper style={{ padding: '24px 20px' }}>
              <LoadingSpinner style={{ width: 32, height: 32, borderWidth: 3 }} />
            </LoadingWrapper>
          )}
        </ContentWrapper>
      )}
    </RightContentDrawer>
  );
};

export default ChangeLogDrawer;
